<template>
  <div class="one-time-access-usage">
    <NavBar />
    
    <div class="container-fluid py-4">
      <div class="row">
        <div class="col-12">
          <div class="card shadow">
            <div class="card-header bg-white border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">One-Time Access Usage</h3>
                  <p class="text-muted mb-0">Track ride managers using one-time access features</p>
                </div>
                <div class="col text-right">
                  <b-form-group label="From" label-size="sm" class="d-inline-block mr-2 mb-0">
                    <b-form-input
                      v-model="startDate"
                      type="date"
                      size="sm"
                      @change="fetchUsageData"
                    />
                  </b-form-group>
                  <b-form-group label="To" label-size="sm" class="d-inline-block mr-3 mb-0">
                    <b-form-input
                      v-model="endDate"
                      type="date"
                      size="sm"
                      :min="startDate"
                      @change="fetchUsageData"
                    />
                  </b-form-group>
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="fetchUsageData"
                    :disabled="loading"
                    class="mr-2"
                  >
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                    Refresh
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="toggleViewMode"
                  >
                    <i class="fas" :class="viewMode === 'summary' ? 'fa-list' : 'fa-chart-bar'"></i>
                    {{ viewMode === 'summary' ? 'Detailed View' : 'Summary View' }}
                  </b-button>
                </div>
              </div>
            </div>
            
            <div class="card-body">
              <!-- Loading State -->
              <div v-if="loading" class="text-center py-5">
                <b-spinner variant="primary" />
                <p class="mt-2">Loading usage data...</p>
              </div>
              
              <!-- Error State -->
              <div v-else-if="error" class="text-center py-5">
                <i class="fas fa-exclamation-triangle text-danger fa-3x mb-3"></i>
                <h5 class="text-danger">Error Loading Usage Data</h5>
                <p class="text-muted">{{ error }}</p>
                <b-button variant="primary" @click="fetchUsageData">
                  Try Again
                </b-button>
              </div>
              
              <!-- No Data State -->
              <div v-else-if="!usageData || usageData.length === 0" class="text-center py-5">
                <i class="fas fa-link text-muted fa-3x mb-3"></i>
                <h5 class="text-muted">No One-Time Access Usage Found</h5>
                <p class="text-muted">No ride managers have used one-time access features yet.</p>
              </div>
              
              <!-- Usage Data Display -->
              <div v-else>
                <!-- Summary Cards -->
                <div class="row mb-4">
                  <div class="col-md-4">
                    <div class="card bg-primary text-white">
                      <div class="card-body">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <h6 class="card-title text-uppercase mb-1">Active Managers</h6>
                            <h3 class="mb-0">{{ usageData.length }}</h3>
                          </div>
                          <div class="ml-3">
                            <i class="fas fa-users fa-2x opacity-75"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-4">
                    <div class="card bg-info text-white">
                      <div class="card-body">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <h6 class="card-title text-uppercase mb-1">Total Rides</h6>
                            <h3 class="mb-0">{{ totalRides }}</h3>
                          </div>
                          <div class="ml-3">
                            <i class="fas fa-route fa-2x opacity-75"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-4">
                    <div class="card bg-success text-white">
                      <div class="card-body">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <h6 class="card-title text-uppercase mb-1">Total Accesses</h6>
                            <h3 class="mb-0">{{ totalAccesses }}</h3>
                          </div>
                          <div class="ml-3">
                            <i class="fas fa-link fa-2x opacity-75"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Summary View -->
                <div v-if="viewMode === 'summary'">
                  <h5 class="mb-3">Manager Summary</h5>
                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead class="thead-light">
                        <tr>
                          <th>Manager</th>
                          <th>Email</th>
                          <th>Rides</th>
                          <th>Accesses</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="manager in usageData" :key="manager.manager_id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-circle bg-primary text-white mr-3">
                                {{ manager.manager_username ? manager.manager_username.charAt(0).toUpperCase() : 'M' }}
                              </div>
                              <div>
                                <strong>{{ manager.manager_username || 'Unknown Manager' }}</strong>
                                <br>
                                <small class="text-muted">ID: {{ manager.manager_id }}</small>
                              </div>
                            </div>
                          </td>
                          <td>{{ manager.manager_email || 'N/A' }}</td>
                          <td>
                            <span class="badge badge-info">
                              {{ manager.total_rides || 0 }}
                            </span>
                          </td>
                          <td>
                            <span class="badge badge-success">
                              {{ manager.total_accesses || 0 }}
                            </span>
                          </td>
                          <td>
                            <b-button
                              variant="outline-primary"
                              size="sm"
                              @click="viewManagerDetails(manager)"
                            >
                              <i class="fas fa-eye"></i>
                              View Details
                            </b-button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <!-- Detailed View -->
                <div v-else>
                  <h5 class="mb-3">Detailed Usage by Manager</h5>
                  <div v-for="manager in usageData" :key="manager.manager_id" class="mb-4">
                    <div class="card">
                      <div class="card-header bg-light">
                        <div class="row align-items-center">
                          <div class="col">
                            <h6 class="mb-0">
                              <i class="fas fa-user mr-2"></i>
                              {{ manager.manager_username || 'Unknown Manager' }}
                              <small class="text-muted">(ID: {{ manager.manager_id }})</small>
                            </h6>
                            <small class="text-muted">{{ manager.manager_email || 'N/A' }}</small>
                          </div>
                          <div class="col text-right">
                            <span class="badge badge-info mr-2">{{ manager.total_rides }} Rides</span>
                            <span class="badge badge-success">{{ manager.total_accesses }} Accesses</span>
                          </div>
                        </div>
                      </div>
                      <div class="card-body">
                        <div v-if="manager.rides && manager.rides.length > 0">
                          <div v-for="ride in manager.rides" :key="ride.ride_id" class="mb-3">
                            <div class="border-left border-primary pl-3">
                              <div class="d-flex align-items-center justify-content-between mb-2">
                                <h6 class="mb-0">
                                  <i class="fas fa-route mr-2"></i>
                                  {{ ride.ride_title || 'Untitled Ride' }}
                                  <small class="text-muted">(ID: {{ ride.ride_id }})</small>
                                </h6>
                                <b-button
                                  variant="outline-secondary"
                                  size="sm"
                                  @click="copyRideData(ride)"
                                  :disabled="!ride.users || ride.users.length === 0"
                                  :title="'Copy all access records for this ride'"
                                >
                                  <i class="fas" :class="copiedRideId === ride.ride_id ? 'fa-check text-success' : 'fa-copy'"></i>
                                </b-button>
                              </div>
                              <div class="row mb-2">
                                <div class="col-md-3">
                                  <small class="text-muted">
                                    <strong>Duration:</strong> {{ ride.duration_days }} days
                                  </small>
                                </div>
                                <div class="col-md-3">
                                  <small class="text-muted">
                                    <strong>Start:</strong> {{ formatDate(ride.ride_start_date) }}
                                  </small>
                                </div>
                                <div class="col-md-3">
                                  <small class="text-muted">
                                    <strong>End:</strong> {{ formatDate(ride.ride_end_date) }}
                                  </small>
                                </div>
                                <div class="col-md-3">
                                  <small class="text-muted">
                                    <strong>Accesses:</strong> {{ ride.users ? ride.users.length : 0 }}
                                  </small>
                                </div>
                              </div>
                              
                              <!-- User Access Details -->
                              <div v-if="ride.users && ride.users.length > 0" class="mt-2">
                                <small class="text-muted mb-2 d-block">
                                  <strong>Access Records:</strong>
                                </small>
                                <div class="table-responsive">
                                  <table class="table table-sm table-striped">
                                    <thead>
                                      <tr>
                                        <th>Usage ID</th>
                                        <th>User ID</th>
                                        <th>Message</th>
                                        <th>Created At</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="user in ride.users" :key="user.usage_id">
                                        <td>
                                          <b-badge variant="secondary">{{ user.usage_id }}</b-badge>
                                        </td>
                                        <td>
                                          <span v-if="user.user_id" class="text-primary">
                                            {{ user.user_id }}
                                          </span>
                                          <span v-else class="text-muted">Anonymous</span>
                                        </td>
                                        <td>
                                          <span v-if="user.message" class="text-dark">
                                            {{ user.message }}
                                          </span>
                                          <span v-else class="text-muted">No message</span>
                                        </td>
                                        <td>
                                          <span class="text-success">
                                            {{ formatDateTime(user.created_time) }}
                                          </span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                              <div v-else class="text-muted">
                                <small>No access records found</small>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-center py-3">
                          <p class="text-muted">No rides with one-time access usage</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Manager Details Modal -->
    <b-modal
      v-model="showManagerModal"
      :title="`Detailed Usage - ${selectedManager?.manager_username || 'Manager'}`"
      size="xl"
      @hide="selectedManager = null"
    >
      <div v-if="selectedManager">
        <div class="row mb-3">
          <div class="col-md-6">
            <strong>Manager ID:</strong> {{ selectedManager.manager_id }}
          </div>
          <div class="col-md-6">
            <strong>Email:</strong> {{ selectedManager.manager_email || 'N/A' }}
          </div>
        </div>
        
        <div class="row mb-3">
          <div class="col-md-6">
            <strong>Total Rides:</strong> {{ selectedManager.total_rides || 0 }}
          </div>
          <div class="col-md-6">
            <strong>Total Accesses:</strong> {{ selectedManager.total_accesses || 0 }}
          </div>
        </div>
        
        <h6 class="mt-4 mb-3">Ride Details:</h6>
        <div v-if="selectedManager.rides && selectedManager.rides.length > 0">
          <div v-for="ride in selectedManager.rides" :key="ride.ride_id" class="mb-4">
            <div class="card">
              <div class="card-header bg-light d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="mb-0">
                    {{ ride.ride_title || 'Untitled Ride' }}
                    <small class="text-muted">(ID: {{ ride.ride_id }})</small>
                  </h6>
                  <small class="text-muted">
                    {{ ride.duration_days }} days •
                    {{ formatDate(ride.ride_start_date) }} - {{ formatDate(ride.ride_end_date) }}
                  </small>
                </div>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="copyRideData(ride)"
                  :disabled="!ride.users || ride.users.length === 0"
                  :title="'Copy all access records for this ride'"
                >
                  <i class="fas" :class="copiedRideId === ride.ride_id ? 'fa-check text-success' : 'fa-copy'"></i>
                </b-button>
              </div>
              <div class="card-body">
                <div v-if="ride.users && ride.users.length > 0">
                  <h6 class="mb-3">Access Records ({{ ride.users.length }}):</h6>
                  <div class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Usage ID</th>
                          <th>User ID</th>
                          <th>Message</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="user in ride.users" :key="user.usage_id">
                          <td>
                            <b-badge variant="secondary">{{ user.usage_id }}</b-badge>
                          </td>
                          <td>
                            <span v-if="user.user_id" class="text-primary">
                              {{ user.user_id }}
                            </span>
                            <span v-else class="text-muted">Anonymous</span>
                          </td>
                          <td>
                            <span v-if="user.message" class="text-dark">
                              {{ user.message }}
                            </span>
                            <span v-else class="text-muted">No message</span>
                          </td>
                          <td>
                            <span class="text-success">
                              {{ formatDateTime(user.created_time) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-else class="text-center py-3">
                  <p class="text-muted">No access records found</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-3">
          <p class="text-muted">No rides with one-time access usage</p>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBar from '@/components/NavBar.vue';
import rideInformationAPI from '@/api/rideInformation';

import { INTERNAL_HOME_PATH } from '@/common/appHome';
export default {
  name: 'OneTimeAccessUsage',
  
  components: {
    NavBar
  },
  
  data() {
    return {
      loading: false,
      error: null,
      usageData: [],
      selectedManager: null,
      showManagerModal: false,
      viewMode: 'summary', // 'summary' or 'detailed'
      startDate: null,
      endDate: null,
      copiedRideId: null
    };
  },
  
  computed: {
    ...mapGetters([
      'isUserManager',
      'apptoken'
    ]),
    
    totalRides() {
      if (!this.usageData || this.usageData.length === 0) return 0;
      return this.usageData.reduce((total, manager) => total + (manager.total_rides || 0), 0);
    },
    
    totalAccesses() {
      if (!this.usageData || this.usageData.length === 0) return 0;
      return this.usageData.reduce((total, manager) => total + (manager.total_accesses || 0), 0);
    }
  },
  
  created() {
    if (!this.isUserManager) {
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    
    this.fetchUsageData();
  },
  
  methods: {
    async fetchUsageData() {
      this.loading = true;
      this.error = null;
      
      try {
        console.log("Fetching one-time access usage data");
        const response = await rideInformationAPI.getOneTimeAccessUsageByManager(
          this.apptoken,
          this.startDate,
          this.endDate
        );
        
        console.log("Usage data response:", response.data);
        
        if (response.data && response.data.ServerMassageCode === 'success') {
          this.usageData = response.data.usage_data || [];
          console.log("Processed usage data:", this.usageData);
        } else {
          this.error = response.data?.message || 'Failed to load usage data';
        }
      } catch (error) {
        console.error('Failed to fetch usage data:', error);
        this.error = error.response?.data?.message || 'Failed to load usage data';
      }
      
      this.loading = false;
    },
    
    toggleViewMode() {
      this.viewMode = this.viewMode === 'summary' ? 'detailed' : 'summary';
    },
    
    viewManagerDetails(manager) {
      console.log("Viewing manager details:", manager);
      this.selectedManager = manager;
      this.showManagerModal = true;
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return 'Invalid Date';
      }
    },
    
    copyRideData(ride) {
      if (!ride.users || ride.users.length === 0) return;
      const text = ride.users
        .map(u => `${u.message || ''}\t${this.formatDateTime(u.created_time)}`)
        .join('\n');
      navigator.clipboard.writeText(text);
      this.copiedRideId = ride.ride_id;
      setTimeout(() => { this.copiedRideId = null; }, 1500);
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return 'Invalid Date';
      }
    }
  }
};
</script>

<style scoped>
.one-time-access-usage {
  min-height: 100vh;
  background-color: #f8f9fe;
  padding-top: 72px; /* Add padding to account for fixed navbar */
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
}

.opacity-75 {
  opacity: 0.75;
}

.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.btn {
  transition: all 0.2s;
}

.btn:hover {
  transform: translateY(-1px);
}

/* Custom scrollbar for table */
.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.table-responsive::-webkit-scrollbar {
  width: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Summary cards styling */
.card.bg-primary,
.card.bg-info,
.card.bg-success {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.card.bg-primary:hover,
.card.bg-info:hover,
.card.bg-success:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

/* Modal styling */
.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

/* Border left styling */
.border-left {
  border-left: 4px solid #007bff !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .one-time-access-usage {
    padding-top: 60px;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .avatar-circle {
    width: 30px;
    height: 30px;
    font-size: 14px;
  }
}

/* Badge improvements */
.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.5rem;
}

/* Form input styling */
.form-control {
  max-width: 200px;
}
</style>
